<script setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import Switch from 'dashboard/components-next/switch/Switch.vue';

const props = defineProps({
  modelValue: {
    type: Object,
    default: () => ({}),
  },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

// Ordem de exibição: segunda→domingo. Chave = wday do Ruby (domingo=0),
// mesma convenção do Agno::ScheduleGate.
const DAY_ORDER = ['1', '2', '3', '4', '5', '6', '0'];
const DAY_LABELS = {
  0: 'SUN',
  1: 'MON',
  2: 'TUE',
  3: 'WED',
  4: 'THU',
  5: 'FRI',
  6: 'SAT',
};

const TIMEZONES = [
  'America/Sao_Paulo',
  'America/Fortaleza',
  'America/Recife',
  'America/Bahia',
  'America/Belem',
  'America/Manaus',
  'America/Cuiaba',
  'America/Campo_Grande',
  'America/Porto_Velho',
  'America/Rio_Branco',
  'America/Noronha',
  'Europe/Lisbon',
];

const config = computed(() => ({
  enabled: false,
  timezone: 'America/Sao_Paulo',
  days: {},
  ...props.modelValue,
}));

const update = patch =>
  emit('update:modelValue', { ...config.value, ...patch });

const dayConfig = wday => ({
  enabled: false,
  start: '08:00',
  end: '18:00',
  ...(config.value.days?.[wday] || {}),
});

const updateDay = (wday, patch) =>
  update({
    days: { ...config.value.days, [wday]: { ...dayConfig(wday), ...patch } },
  });

// Fim menor que o início = janela que atravessa a meia-noite (ex.: 22h→06h).
const isOvernight = wday => {
  const day = dayConfig(wday);
  return day.enabled && day.start !== day.end && day.end < day.start;
};
</script>

<template>
  <div class="space-y-5">
    <div class="flex items-center justify-between p-4 bg-n-alpha-1 rounded-xl">
      <div>
        <h4 class="text-sm font-medium text-n-slate-12">
          {{ t('AI_AGENTS.SCHEDULE.ENABLED') }}
        </h4>
        <p class="text-xs text-n-slate-11 mt-0.5">
          {{ t('AI_AGENTS.SCHEDULE.ENABLED_DESCRIPTION') }}
        </p>
      </div>
      <Switch
        :model-value="config.enabled"
        @update:model-value="update({ enabled: $event })"
      />
    </div>

    <template v-if="config.enabled">
      <div class="max-w-72">
        <label class="block text-sm font-medium text-n-slate-12 mb-1.5">
          {{ t('AI_AGENTS.SCHEDULE.TIMEZONE') }}
        </label>
        <select
          :value="config.timezone"
          class="w-full px-3 py-2 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12 text-sm"
          @change="update({ timezone: $event.target.value })"
        >
          <option v-for="tz in TIMEZONES" :key="tz" :value="tz">
            {{ tz }}
          </option>
        </select>
      </div>

      <div class="space-y-2">
        <div
          v-for="wday in DAY_ORDER"
          :key="wday"
          class="flex items-center gap-4 p-3 bg-n-alpha-1 rounded-xl"
        >
          <div class="flex items-center gap-3 w-40">
            <Switch
              :model-value="dayConfig(wday).enabled"
              @update:model-value="updateDay(wday, { enabled: $event })"
            />
            <span class="text-sm text-n-slate-12">
              {{ t(`AI_AGENTS.SCHEDULE.DAYS.${DAY_LABELS[wday]}`) }}
            </span>
          </div>

          <template v-if="dayConfig(wday).enabled">
            <div class="flex items-center gap-2">
              <input
                type="time"
                :value="dayConfig(wday).start"
                class="px-2 py-1.5 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12 text-sm"
                @change="updateDay(wday, { start: $event.target.value })"
              />
              <span class="text-xs text-n-slate-11">
                {{ t('AI_AGENTS.SCHEDULE.UNTIL') }}
              </span>
              <input
                type="time"
                :value="dayConfig(wday).end"
                class="px-2 py-1.5 bg-n-alpha-1 border border-n-weak rounded-lg text-n-slate-12 text-sm"
                @change="updateDay(wday, { end: $event.target.value })"
              />
            </div>
            <span v-if="isOvernight(wday)" class="text-xs text-n-slate-11">
              {{ t('AI_AGENTS.SCHEDULE.OVERNIGHT') }}
            </span>
          </template>
          <span v-else class="text-xs text-n-slate-11">
            {{ t('AI_AGENTS.SCHEDULE.DAY_OFF') }}
          </span>
        </div>
      </div>

      <p class="text-xs text-n-slate-11">
        {{ t('AI_AGENTS.SCHEDULE.HINT') }}
      </p>
    </template>
  </div>
</template>
